import React from 'react'

import styled from '@emotion/styled'

import Icon from './Icon'

export type DetailsProps = {
  summary: React.ReactNode
  open?: boolean
  onToggle?: () => void
  children: React.ReactNode
}

const SUMMARY_LINE_HEIGHT = '1.5rem'
const DETAILS_ARROW_WIDTH = '1rem'
const DETAILS_ARROW_MARGIN = '0.25rem'

const DetailsContainer = styled.details`
  outline: 0;
`

const DetailsArrow = styled.div`
  cursor: pointer;
  margin-right: ${DETAILS_ARROW_MARGIN};
  font-size: ${DETAILS_ARROW_WIDTH};
  height: ${SUMMARY_LINE_HEIGHT};
  display: flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  align-self: flex-start;

  details[open] & {
    transform: rotate(90deg);
    transform-origin: center;
  }
`

const Summary = styled.summary`
  outline: 0;
  margin: 0;
  cursor: default;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  /* hide details arrow in Firefox */
  list-style: none;

  /* hide details arrow in Chrome / Safari */
  ::-webkit-details-marker {
    display: none;
  }
`

const SummaryContent = styled.div`
  display: flex;
  font-size: 1.25rem;
  line-height: 1.5rem;
`

const SummaryHeading = styled.div`
  font-size: 1.125rem;
  line-height: ${SUMMARY_LINE_HEIGHT};
  font-weight: ${props => props.theme.weights.medium};
  flex-grow: 1;
  color: ${props => props.theme.colors.label};
`

const DetailsContent = styled.div`
  ${props => props.theme.typography.bodyText};
  color: ${props => props.theme.colors.secondaryLabel};
  padding-left: calc(${DETAILS_ARROW_WIDTH} + ${DETAILS_ARROW_MARGIN});
  margin-top: 1rem;
`

const Details: React.FC<DetailsProps> = ({ summary, open, onToggle, children }) => {
  // Different behavior is needed due to how React treat's <details> components
  // https://github.com/facebook/react/issues/15486#issuecomment-873516817
  const isControlled = open != null

  return (
    <DetailsContainer
      open={open}
      onToggle={() => {
        if (!isControlled) {
          onToggle?.()
        }
      }}
    >
      <Summary
        onClick={event => {
          if (isControlled) {
            event.preventDefault()
            onToggle?.()
          }
        }}
      >
        <SummaryContent>
          <DetailsArrow>
            <Icon name="chevron-right" width="1em" height="1.25em" />
          </DetailsArrow>
          <SummaryHeading>{summary}</SummaryHeading>
        </SummaryContent>
      </Summary>
      <DetailsContent>{children}</DetailsContent>
    </DetailsContainer>
  )
}

export default Details
